import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useCallback, useState } from 'react'
import {
  applyComposerDragDelta,
  type ComposerDemand,
  type ComposerHeights,
  type ComposerOverrides,
} from '../components/ChatInput/composerDrag'

interface UseComposerDragParams {
  /** The composer's currently rendered heights — the drag's starting point. */
  getStartHeights: () => ComposerHeights
  getDemand: () => ComposerDemand
  onOverridesChange: (next: ComposerOverrides) => void
  keyboardStep: number
}

/**
 * Wires up the resize handle's drag and keyboard interactions: tracking the pointer, adding and
 * removing the global mouse listeners while a drag is in progress, and reporting the result.
 *
 * It never decides how many pixels come off context vs. input itself — on every move, it just
 * asks applyComposerDragDelta (composerDrag.ts) for the new heights and passes them straight on.
 */
export function useComposerDrag({
  getStartHeights,
  getDemand,
  onOverridesChange,
  keyboardStep,
}: UseComposerDragParams) {
  const [isDragging, setIsDragging] = useState(false)

  const applyStep = useCallback(
    (deltaY: number) => {
      const startHeights = getStartHeights()
      const demand = getDemand()
      onOverridesChange(applyComposerDragDelta(startHeights, deltaY, demand))
    },
    [getStartHeights, getDemand, onOverridesChange]
  )

  const onMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      setIsDragging(true)

      const dragStartY = event.clientY
      const startHeights = getStartHeights()
      const previousUserSelect = document.body.style.userSelect
      const previousCursor = document.body.style.cursor

      // While dragging, block wheel/touch scrolling so the handle interaction does not also
      // scroll the editor or the page when height is clamped.
      const preventDragScroll = (scrollEvent: Event) => {
        scrollEvent.preventDefault()
      }

      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'ns-resize'

      const handleDragMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault()

        // On Windows the mouseup can happen outside the task pane and never reach us,
        // so a move with no button held means the drag is already over.
        if (moveEvent.buttons === 0) {
          endDrag()
          return
        }

        const deltaY = moveEvent.clientY - dragStartY
        onOverridesChange(applyComposerDragDelta(startHeights, deltaY, getDemand()))
      }

      const endDrag = () => {
        // Always restore global listeners/styles to avoid sticky drag state.
        setIsDragging(false)
        window.removeEventListener('mousemove', handleDragMove)
        window.removeEventListener('mouseup', endDrag)
        window.removeEventListener('blur', endDrag)
        window.removeEventListener('wheel', preventDragScroll, true)
        window.removeEventListener('touchmove', preventDragScroll, true)
        document.body.style.userSelect = previousUserSelect
        document.body.style.cursor = previousCursor
      }

      window.addEventListener('mousemove', handleDragMove)
      window.addEventListener('mouseup', endDrag)
      window.addEventListener('blur', endDrag)
      window.addEventListener('wheel', preventDragScroll, { passive: false, capture: true })
      window.addEventListener('touchmove', preventDragScroll, { passive: false, capture: true })
    },
    [getStartHeights, getDemand, onOverridesChange]
  )

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        applyStep(-keyboardStep)
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        applyStep(keyboardStep)
      }
    },
    [applyStep, keyboardStep]
  )

  return { isDragging, onMouseDown, onKeyDown }
}
